import { useEffect, useState } from "react";
import { api, errMsg, formatDateTime, downloadFile } from "@/lib/api";
import { Download, FileSpreadsheet, Link2, Loader2, RefreshCw, Upload } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import ProjectMatcher from "@/components/ProjectMatcher";

const LABELS = { clients: "Klien", venues: "Venue", projects: "Project", payments: "Pembayaran klien", expenses: "Pengeluaran (petty cash)", topups: "Kas masuk (petty cash)" };

function Summary({ s, committed }) {
  return (
    <div className="card-editorial p-5 space-y-4" data-testid={committed ? "import-result" : "import-preview"}>
      <h2 className="font-serif-display text-xl font-semibold">{committed ? "Impor selesai" : "Pratinjau impor"}</h2>
      <p className="text-xs text-[#8A999A]">Dibaca: {s.rows.jobs} baris Job Summary · {s.rows.payments} pembayaran · {s.rows.cash} transaksi Petty Cash</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(LABELS).map(([k, l]) => (
          <div key={k} className="p-3 bg-[#FAF7F2] rounded-lg" data-testid={`import-count-${k}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#B87333]">{l}</div>
            <div className="font-mono-num text-lg font-semibold">{s[k].new} <span className="text-xs text-[#536263]">baru</span></div>
            <div className="text-xs text-[#8A999A]">{s[k].updated} diperbarui · {s[k].unchanged ?? 0} sama</div>
          </div>
        ))}
      </div>
      {s.unmatched_payments.length > 0 && (
        <div className="text-sm" data-testid="import-unmatched-payments">
          <div className="font-medium text-amber-800">Pembayaran tanpa project di Job Summary (dilewati):</div>
          <div className="text-xs text-[#536263]">{s.unmatched_payments.join(", ")}</div>
        </div>
      )}
      {s.unmatched_cash_projects.length > 0 && (
        <div className="text-sm" data-testid="import-unmatched-cash">
          <div className="font-medium text-amber-800">Nama project di Petty Cash yang tidak cocok dengan Job Summary:</div>
          <div className="text-xs text-[#536263]">Transaksi tetap diimpor tanpa project (nama asli disimpan): {s.unmatched_cash_projects.join(", ")}</div>
        </div>
      )}
    </div>
  );
}

export default function ImportSheet() {
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [config, setConfig] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  async function toggleAuto(enabled) {
    setErr("");
    try { await api.post("/sheet/auto", { enabled }); loadConfig(); } catch (ex) { setErr(errMsg(ex)); }
  }

  const loadConfig = () => api.get("/sheet/config").then(r => { setConfig(r.data); setUrl(u => u || r.data.url || ""); });
  useEffect(() => { loadConfig(); }, []);

  function form() {
    const fd = new FormData();
    if (file) fd.append("file", file);
    else fd.append("url", url);
    return fd;
  }

  async function run(kind) {
    setErr(""); setBusy(kind);
    if (kind === "preview") setResult(null);
    try {
      const { data } = await api.post(kind === "preview" ? "/sheet/import/preview" : "/sheet/import", form());
      if (kind === "preview") setPreview(data); else { setResult(data); setPreview(null); loadConfig(); setRefreshKey(k => k + 1); }
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy("");
  }

  async function exportXlsx() {
    setBusy("export");
    try { await downloadFile("/sheet/export", "puade-finance-os.xlsx"); } catch (ex) { setErr(errMsg(ex)); }
    setBusy("");
  }

  const ready = file || url.trim();
  return (
    <div className="p-4 sm:p-8 space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Impor &amp; Ekspor Sheets</h1>
        <p className="text-sm text-[#536263]">Format "Puade Decor · Finance OS": Job Summary, Status Pembayaran, Petty Cash</p>
        <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
      </header>

      <div className="card-editorial p-5 space-y-4">
        <h2 className="font-serif-display text-xl font-semibold flex items-center gap-2"><FileSpreadsheet size={18} />Impor ke Puade OS</h2>
        <label className="text-xs uppercase tracking-wider text-[#B87333] flex items-center gap-1"><Link2 size={12} />Link Google Sheets (akses: siapa saja dengan link · Viewer)</label>
        <input value={url} onChange={e => { setUrl(e.target.value); setFile(null); }} placeholder="https://docs.google.com/spreadsheets/d/..."
          className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" data-testid="sheet-url-input" />
        <div className="text-xs text-[#8A999A] text-center">atau</div>
        <label className="flex items-center gap-3 border-2 border-dashed border-[#E2DDD3] rounded-lg p-3 cursor-pointer">
          <Upload size={18} className="text-[#B87333]" />
          <span className="text-sm text-[#536263]">{file ? file.name : "Upload file Excel (.xlsx)"}</span>
          <input type="file" accept=".xlsx" className="hidden" onChange={e => { setFile(e.target.files[0] || null); e.target.value = ""; }} data-testid="sheet-file-input" />
        </label>
        <div className="flex gap-2 flex-wrap">
          <button disabled={!ready || busy} onClick={() => run("preview")} className="btn-copper" data-testid="sheet-preview-btn">
            {busy === "preview" && <Loader2 size={16} className="animate-spin" />}Pratinjau
          </button>
          <button disabled={!preview || busy} onClick={() => run("commit")} className="btn-primary disabled:opacity-50" data-testid="sheet-import-btn">
            {busy === "commit" && <Loader2 size={16} className="animate-spin" />}Impor Sekarang
          </button>
        </div>
        <p className="text-[11px] text-[#8A999A]">Aman diulang: data yang sudah pernah diimpor akan diperbarui, tidak dobel. Status project yang sudah diubah di Puade OS tidak ditimpa.</p>
        {config?.last && <p className="text-xs text-[#536263]" data-testid="sheet-last-import">Impor terakhir: {formatDateTime(config.last.at)} oleh {config.last.by}</p>}
        {err && <div className="text-sm text-red-700" data-testid="sheet-error">{err}</div>}
      </div>

      {preview && <Summary s={preview} />}
      {result && <Summary s={result} committed />}

      <div className="card-editorial p-5 space-y-3" data-testid="auto-sync-card">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif-display text-xl font-semibold flex items-center gap-2"><RefreshCw size={18} />Sinkron Otomatis</h2>
          <Switch checked={!!config?.auto} onCheckedChange={toggleAuto} data-testid="auto-sync-switch" />
        </div>
        <p className="text-sm text-[#536263]">Setiap 15 menit, Puade OS mengambil data terbaru dari link Google Sheets yang tersimpan. Baris yang tidak berubah di sheet tidak akan menimpa data di Puade OS.</p>
        {config?.url && <p className="text-xs text-[#8A999A] break-all">Link: {config.url}</p>}
        {config?.last_auto && (
          <p className={`text-xs ${config.last_auto.result === "error" ? "text-red-700" : "text-[#536263]"}`} data-testid="auto-sync-last">
            Sinkron terakhir: {formatDateTime(config.last_auto.at)} · {config.last_auto.result === "ok"
              ? `${Object.values(config.last_auto.changes || {}).reduce((a, b) => a + b, 0)} baris berubah`
              : config.last_auto.result === "error" ? `gagal: ${config.last_auto.error}` : "dilewati"}
          </p>
        )}
      </div>

      <ProjectMatcher refreshKey={refreshKey} />

      <div className="card-editorial p-5 space-y-3">
        <h2 className="font-serif-display text-xl font-semibold flex items-center gap-2"><Download size={18} />Ekspor dari Puade OS</h2>
        <p className="text-sm text-[#536263]">Unduh Excel dengan kolom yang sama seperti spreadsheet Anda (Job Summary, Status Pembayaran, Petty Cash) berisi data terbaru dari Puade OS.</p>
        <button onClick={exportXlsx} disabled={!!busy} className="btn-primary" data-testid="sheet-export-btn">
          {busy === "export" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}Unduh Excel
        </button>
      </div>

      <div className="p-4 rounded-lg border border-[#B87333]/30 bg-[#F7EFE8] text-sm text-[#536263]">
        Saat ini sinkron berjalan satu arah (Sheets → Puade OS). Arah sebaliknya (Puade OS → Sheets) aktif setelah Google Client ID &amp; Secret dihubungkan. Sementara itu, pakai tombol Unduh Excel.
      </div>
    </div>
  );
}
